import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { almacen, newOperationId, type Production } from '../api';
import {
  Boton,
  Cabecera,
  Cargando,
  Fallo,
  Pastilla,
  Rotulo,
  Tarjeta,
  Vacio,
} from '../componentes';
import { cantidad, soles } from '../formato';
import { EntradaInvalida, leerPositivo } from '../numeros';
import { FichaDeProduccion } from '../formularios';

/**
 * Las sub-recetas de cocina: lo que se prepara para preparar.
 *
 * Cierra el círculo del almacén. La leche de tigre sale de limón, ají y pescado
 * —que bajan— y **vuelve como un insumo más**, con su lote y su costo por litro
 * calculado de lo que costó hacerla esa tarde. A partir de ahí un ceviche la
 * consume igual que consumiría un limón.
 */
export function Produccion() {
  const clientes = useQueryClient();
  const consulta = useQuery({
    queryKey: ['producciones'],
    queryFn: almacen.productions,
  });

  const producciones = consulta.data ?? [];

  return (
    <>
      <Cabecera
        titulo="Producción interna"
        subtitulo="Sub-recetas que consumen insumos y devuelven otro" ayuda="Lo que se prepara para preparar: la leche de tigre, los fondos. Gasta ingredientes y devuelve al almacén lo producido, con su costo calculado."
        acciones={<FichaDeProduccion />}
      />

      <div className="cuerpo">
        {consulta.isError ? (
          <Fallo error={consulta.error} onReintentar={() => consulta.refetch()} />
        ) : null}

        <p className="aviso-caja">
          Cada producción descarga sus insumos y crea un lote nuevo del
          sub-producto, con su propio costo por unidad. El costo no se teclea:
          sale de lo que costaron los ingredientes hoy, para que el food cost de
          todo lo que la use no se apoye en un número de hace tres meses.
        </p>

        {consulta.isLoading ? (
          <Cargando que="las producciones" />
        ) : producciones.length === 0 ? (
          <Vacio
            titulo="Ninguna producción cargada"
            mensaje="Una sub-receta es una receta que en vez de venderse vuelve al almacén: la leche de tigre, el fondo de pollo, la chicha concentrada. Se dan de alta como un insumo más y se les carga su receta aquí."
            accion={<FichaDeProduccion />}
          />
        ) : (
          <div className="rejilla-tarjetas">
            {producciones.map((produccion) => (
              <Ficha
                key={produccion.id}
                produccion={produccion}
                onProducido={() => void clientes.invalidateQueries()}
              />
            ))}
          </div>
        )}
      </div>
    </>
  );
}

function Ficha({
  produccion,
  onProducido,
}: {
  produccion: Production;
  onProducido: () => void;
}) {
  const [tandas, setTandas] = useState('1');
  // El mismo en cada intento: un reintento tras un corte no produce dos veces.
  const [operationId, setOperationId] = useState(newOperationId);

  const producir = useMutation({
    mutationFn: () =>
      almacen.produce({
        operationId,
        recipeId: produccion.id,
        // «1,5» son tanda y media; antes se leía como 1, y «0» como 1 también.
        batches: tandasValidas(tandas),
      }),
    onSuccess: () => {
      setOperationId(newOperationId());
      onProducido();
    },
  });

  return (
    <Tarjeta>
      <div className="proveedor__cabeza">
        <div className="tarjeta__nombre-caja">
          <strong className="tarjeta__nombre">
            {produccion.name}
          </strong>
          <p className="nota-sm tarjeta__sub">
            Devuelve {produccion.produces} · rinde{' '}
            {cantidad(produccion.yieldQuantity)} por tanda
          </p>
        </div>
        {produccion.ingredientsWithoutCost > 0 ? (
          <Pastilla texto="costo incompleto" tono="aviso" />
        ) : null}
        <FichaDeProduccion produccion={produccion} />
      </div>

      <ul className="lista" style={{ marginTop: 'var(--hueco-md)' }}>
        {produccion.lines.map((line) => (
          <li key={line.supplyId} className="lista__fila">
            <div className="lista__texto">
              <strong>{line.supply}</strong>
              <span className="apagado mono">
                {cantidad(line.quantity)} {line.unit} por tanda
              </span>
            </div>
            {line.hasCost ? (
              <span className="mono">{soles(line.lineCost)}</span>
            ) : (
              <Pastilla texto="sin costo" tono="aviso" />
            )}
          </li>
        ))}
      </ul>

      <dl className="resumen-pares">
        <div>
          <dt>Costo de la tanda</dt>
          <dd className="mono">{soles(produccion.ingredientsCost)}</dd>
        </div>
        <div>
          <dt>Costo por unidad</dt>
          <dd className="mono">
            <strong>{soles(produccion.cost)}</strong>
          </dd>
        </div>
      </dl>

      <div className="formulario__fila" style={{ alignItems: 'end' }}>
        <div className="campo">
          <Rotulo htmlFor={`tandas-${produccion.id}`} ayuda="Cuántas veces se prepara la receta. 1,5 es tanda y media. Baja los ingredientes y entra lo producido.">Tandas</Rotulo>
          <input
            id={`tandas-${produccion.id}`}
            inputMode="decimal"
            value={tandas}
            onChange={(e) => setTandas(e.target.value)}
          />
        </div>
        <Boton
          variante="primario"
          onClick={() => producir.mutate()}
          disabled={producir.isPending}
        >
          {producir.isPending ? 'Produciendo…' : 'Producir'}
        </Boton>
      </div>

      {producir.isError ? <Fallo error={producir.error} /> : null}

      {producir.isSuccess ? (
        <>
          <p className="aviso-caja" style={{ marginTop: 'var(--hueco-md)' }}>
            Hecho. {producir.data.lotId === null ? 'Entró al almacén' : 'El lote nuevo entró al almacén'} y{' '}
            <span className="mono">{produccion.produces}</span> queda en{' '}
            <span className="mono">{cantidad(producir.data.quantity)}</span>, a{' '}
            {soles(producir.data.unitCost)} la unidad.
          </p>
          {producir.data.shortIngredients.length > 0 ? (
            /*
             * La tanda ya se hizo, así que se registra igual. Pero si el libro
             * decía que no alcanzaba, algo no se apuntó: una recepción que no
             * entró o una merma sin registrar. Se dice ahora, no en el conteo.
             */
            <p className="aviso-caja aviso-caja--aviso">
              Según el almacén no alcanzaba {producir.data.shortIngredients.join(', ')}: quedó en
              negativo. Revisa si falta registrar una recepción.
            </p>
          ) : null}
        </>
      ) : null}
    </Tarjeta>
  );
}

/** Las tandas tecleadas, con coma o punto. Cero o nada no es una producción. */
function tandasValidas(texto: string): number {
  const valor = leerPositivo(texto);
  if (Number.isNaN(valor)) {
    throw new EntradaInvalida('Las tandas van en positivo: por ejemplo 1 o 1,5.');
  }
  return valor;
}
